import {forwardRef} from "react";
import {Project, UnitSystem} from "../../../../api/Projects";
import {useUser} from "../../../../misc/Permission";
import {PDF} from "../../../../misc/pdf/parts/PDF";
import {PDFProjectContext} from "../../shopdrawing/openings/OpeningsPDF";
import {PriceLine2} from "../../../../api/Pricing";
import {DataSplitInfo, PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {pdfTableStyle} from "../../../../misc/pdf/parts/Page";
import {ContainerPage} from "../../../../misc/pdf/parts/ContainerPage";
import {useReadyCheck} from "../../../../misc/pdf/UseReadyCheck";
import {RedactedContext} from "../../../../misc/pdf/RedactedContext";
import {QuotePriceLine} from "../../../../api/QuotePricing";
import {fixFinishName} from "../../../../api/Products";
import {isNumber} from "../../../logistics/routes/QtyWithPreview";
import {universalParser} from "../../../../misc/scroller/Table";
import {MetricDistance} from "../../../../misc/MetricDistance";
import {InventoryKind, InventoryRequest} from "../../../../api/Logistics";
import {formatPreps} from "../../shopdrawing/extras/preps/PrepUtil";

export type PriceKind = "hardware" | "frame" | "door" | "glazing" | "div10/wr-accessory" | "div10/wr-partition" | "div10/locker" | "div10/mailbox" | "div10/corner-guard";

export const PriceRequestPDF = forwardRef(function (props: {
    project: Project;
    kind: PriceKind | InventoryKind;
    keyName?: string;
    items: (PriceLine2|QuotePriceLine|InventoryRequest)[]
    includePreps?: boolean;
    onReady(): void;
}, ref: any) {
    const usr = useUser();
    const ready = useReadyCheck(props.onReady);
    ready.require(!!usr);

    return (
        <div ref={ref}>
            <PDF>
                <PDFProjectContext.Provider value={props.project}>
                    <RedactedContext.Provider value={true}>
                        <PriceRequestTable
                            keyName={props.keyName}
                            items={props.items} kind={props.kind} onReady={ready.section()}
                            includePreps={props.includePreps}
                            unitSystem={props.project.unitSystem}
                        />
                    </RedactedContext.Provider>
                </PDFProjectContext.Provider>
            </PDF>
        </div>
    )
})

export function PriceRequestTable(props: {
    kind: PriceKind | InventoryKind;
    keyName?: string;
    includePreps?: boolean;
    items: (PriceLine2|QuotePriceLine|InventoryRequest)[];
    unitSystem: UnitSystem;
    onReady(): void;
}) {
    return (
        <PageRenderProvider data={props.items} onReady={props.onReady}>
            {(list: DataSplitInfo<PriceLine2|QuotePriceLine|InventoryRequest>) =>
                <ContainerPage>
                    <PriceTable kind={props.kind} keyName={props.keyName} list={list}
                                includePreps={props.includePreps}
                                projectSys={props.unitSystem}
                    />
                </ContainerPage>}
        </PageRenderProvider>
    )
}

function PriceTable(props: {
    kind: PriceKind | InventoryKind;
    keyName?: string;
    list: DataSplitInfo<PriceLine2|QuotePriceLine|InventoryRequest>
    includePreps?: boolean;
    projectSys: UnitSystem;
}) {
    const tbl = MakePriceTable({
        kind: props.kind,
        keyName: props.keyName,
        list: props.list.rows,
        includePreps: !!props.includePreps,
        projectSys: props.projectSys,
    });

    return tbl.toReact();
}

class Table {
    header: string[] = [];
    rows: string[][] = [];

    setHeader(values: string[]) {
        this.header = values;
    }

    addRows(values: string[][]) {
        this.rows.push(...values);
    }

    appendRow(values: string[]) {
        this.rows.push(values);
    }

    toReact() {
        return (
            <table className={pdfTableStyle}>
                <thead>
                <tr>
                    {this.header.map(((h, index) => <th key={index}>{h}</th>))}
                </tr>
                </thead>
                <tbody>
                {this.rows.map((row, index) => <tr key={index}>
                    {row.map((cell, index) => <td key={index}>{cell}</td>)}
                </tr>)}
                </tbody>
            </table>
        )
    }

    toStringArray() {
        return [
            this.header,
            ...this.rows,
        ]
    }
}

export function MakePriceTable(props: {
    kind: PriceKind | InventoryKind;
    keyName?: string;
    list: (PriceLine2|QuotePriceLine|InventoryRequest)[]
    includePreps?: boolean;
    hardwareManufacturerSeparators?: boolean;
    projectSys: UnitSystem;
}) {
    const toMetric = props.projectSys === "metric";

    switch(props.kind) {
        case "div10/wr-accessory": {
            const tbl = new Table();
            tbl.setHeader([
                "Qty", "Product", "Description", "Code", "Finish"
            ]);

            tbl.addRows(props.list.map(v => [
                v.qty.toString(),
                v.categoryName || "",
                v.productName,
                v.productCode,
                fixFinishName(v.finish) as string
            ]));

            return tbl;
        }
        case "div10/wr-partition": {
            const tbl = new Table();
            tbl.setHeader([
                "Mounting", "Material", "Description", "Qty"
            ]);

            tbl.addRows(props.list.map(v => [
                v.div10?.partitionBracing || "",
                v.div10?.partitionCoating || "",
                v.div10?.description || "",
                v.qty.toString()
            ]));

            return tbl;
        }
        case "div10/locker": {
            const tbl = new Table();
            tbl.setHeader([
                "Tiers", "Material", "Sloped Tops", "End Panels", "Base Trim", "Width", "Depth", "Height", "Qty"
            ]);

            tbl.addRows(props.list.map(v => [
                v.div10?.lockerTiers || "",
                v.div10?.lockerCoating || "",
                yesNo(v.div10?.lockerSloped),
                yesNo(v.div10?.lockerEndPanels),
                yesNo(v.div10?.lockerBaseTrim),
                v.dimWidth.toString(),
                v.dimLength.toString(),
                v.dimHeight.toString(),
                v.qty.toString()
            ]));

            return tbl;
        }
        case "div10/mailbox": {
            const tbl = new Table();
            tbl.setHeader([
                "Tenant Units", "Parcel Units", "Loading", "Mounting", "Qty"
            ]);

            tbl.addRows(props.list.map(v => [
                v.div10?.mailboxTenantUnits.toString() || "",
                v.div10?.mailboxParcelUnits.toString() || "",
                v.div10?.mailboxLoading || "",
                v.div10?.mailboxMounting || "",
                v.qty.toString()
            ]));

            return tbl;
        }
        case "div10/corner-guard": {
            const tbl = new Table();
            tbl.setHeader([
                "Type", "Code", "Finish", "Width", "Height", "Qty"
            ]);

            tbl.addRows(props.list.map(v => [
                v.div10?.cornerGuardType || "",
                v.productCode,
                v.div10?.cornerGuardFinish || "",
                v.dimWidth.toString(),
                v.dimHeight.toString(),
                v.qty.toString()
            ]));

            return tbl;
        }
        case "glazing": {
            // glass carries no preps, so one flat table of the properties a supplier quotes on
            const tbl = new Table();
            tbl.setHeader([
                "Type", "Thickness", "Thermal", "Bead", "Width", "Height", "Qty"
            ]);

            tbl.addRows(props.list.map(v => [
                v.glass?.type || "",
                v.glass?.thickness || "",
                yesNo(v.glass?.thermal),
                v.glass?.bead || "",
                v.glass?.width || "",
                v.glass?.height || "",
                v.qty.toString()
            ]));

            return tbl;
        }
        case "frame": {
            if(props.keyName === "frame-prep") {
                const tbl = new Table();
                const header = ["Description", "Qty"];
                tbl.setHeader(header);

                tbl.addRows(props.list.map(v => {

                    const list = [
                        // @ts-ignore
                        v.itemName as any,
                        v.qty.toString(),
                    ]

                    return list;
                }));

                return tbl;
            }

            const tbl = new Table();

            let header = ["Qty", "Series", "Material", "Type", "Screen Elev", "Gauge", "Construction", "Profile", "Width", "Height", "Jamb Depth", "Door Thickness", "Label"];

            if(props.includePreps) {
                header.push("Preps")
            }

            tbl.setHeader(header);

            tbl.addRows(props.list.map(v => {
                if(!v.frame) return [];

                let list = [
                    v.qty.toString(),
                    v.frame.series,
                    v.frame.material,
                    v.frame.type,
                    v.frame.screenElev,
                    v.frame.gauge,
                    v.frame.construction,
                    v.frame.profile,
                    fixSoftMetric(v.frame.width, toMetric),
                    fixSoftMetric(v.frame.height, toMetric),
                    v.frame.jambDepth.toString(),
                    v.frame.doorThickness.toString(),
                    v.frame.label,
                ]

                if(props.includePreps) {
                    list.push(formatPreps(v.frame.preps, "frame"));
                }

                return list;
            }));

            return tbl;
        }

        case "door": {
            if(props.keyName === "door-prep/wd" || props.keyName === "door-prep/hm") {
                const tbl = new Table();
                const header = ["Description", "Qty"];
                tbl.setHeader(header);

                tbl.addRows(props.list.map(v => {

                    const list = [
                        // @ts-ignore
                        v.itemName as any,
                        v.qty.toString(),
                    ]

                    return list;
                }));

                return tbl;
            }

            const tbl = new Table();
            if(props.keyName === "door-wd") {
                let header = ["Qty", "Series", "Material", "Finish", "Core", "Seam", "Elevation", "Width", "Height", "Thickness", "Label"];

                if(props.includePreps) {
                    header.push("Preps")
                }

                tbl.setHeader(header);

                tbl.addRows(props.list.map(v => {
                    if(!v.door) return []
                    let list = [
                        v.qty.toString(),
                        v.door.series,
                        v.door.material,
                        v.door.finish,
                        v.door.core,
                        v.door.seam,
                        v.door.elevation,
                        fixSoftMetric(v.door.width, toMetric),
                        fixSoftMetric(v.door.height, toMetric),
                        v.door.thickness.toString(),
                        v.door.label,
                    ];

                    if(props.includePreps) {
                        list.push(formatPreps(v.door.preps, "door"));
                    }

                    return list;
                }));

            } else if(props.keyName === "door-hm") {
                let header = ["Qty", "Series", "Material", "Gauge", "Core", "Seam", "Elevation", "Width", "Height", "Thickness", "Label"];

                if(props.includePreps) {
                    header.push("Preps");
                }

                tbl.setHeader(header);

                tbl.addRows(props.list.map(v => {
                    if(!v.door) return [];

                    let list = [
                        v.qty.toString(),
                        v.door.series,
                        v.door.material,
                        v.door.gauge,
                        v.door.core,
                        v.door.seam,
                        v.door.elevation,
                        fixSoftMetric(v.door.width, toMetric),
                        fixSoftMetric(v.door.height, toMetric),
                        v.door.thickness.toString(),
                        v.door.label,
                    ];

                    if(props.includePreps) {
                        list.push(formatPreps(v.door.preps, "door"));
                    }

                    return list;
                }));
            } else {
                let header = ["Qty", "Series", "Material", "Core", "Seam", "Elevation", "Width", "Height", "Thickness", "Label"];

                if(props.includePreps) {
                    header.push("Preps")
                }
                tbl.setHeader(header);

                tbl.addRows(props.list.map(v => {
                    if(!v.door) return [];

                    let list = [
                        v.qty.toString(),
                        v.door.series,
                        v.door.material,
                        v.door.core,
                        v.door.seam,
                        v.door.elevation,
                        fixSoftMetric(v.door.width, toMetric),
                        fixSoftMetric(v.door.height, toMetric),
                        v.door.thickness.toString(),
                        v.door.label,
                    ]

                    if(props.includePreps) {
                        list.push(formatPreps(v.door.preps, "door"));
                    }

                    return list;
                }));
            }

            return tbl;
        }
        default:
        case "hardware": {
            const tbl = new Table();
            tbl.setHeader([
                "Qty", "Name", "Code", "Finish", "Length", "Width", "Height"
            ]);

            const getName = (v: InventoryRequest|PriceLine2|QuotePriceLine) => {
                return v.productName || v.categoryName || "";
            }

            if(props.hardwareManufacturerSeparators) {
                props.list.sort((a, b) => {
                    if(a.manufacturerName !== b.manufacturerName) {
                        return a.manufacturerName.localeCompare(b.manufacturerName)
                    }

                    if("categorySortOrder" in a && isNumber(a.categorySortOrder) && "categorySortOrder" in b && isNumber(b.categorySortOrder)) {
                        return a.categorySortOrder - b.categorySortOrder;
                    }

                    return getName(a).localeCompare(getName(b));
                })
            }

            let lastMfgId: number = -1;

            tbl.addRows(props.list.flatMap(v => {
                const rows: string[][] = [];

                if(props.hardwareManufacturerSeparators) {
                    if(lastMfgId !== v.manufacturer) {
                        rows.push([v.manufacturerName]);
                        lastMfgId = v.manufacturer;
                    }
                }

                rows.push([
                    v.qty.toString(),
                    getName(v),
                    v.productCode,
                    fixFinishName(v.finish) as string,
                    v.dimLength.toString(),
                    v.dimWidth.toString(),
                    v.dimHeight.toString()
                ]);

                return rows;
            }));

            return tbl;
        }
    }
}

export function yesNo(tf: boolean | undefined) {
    if(tf === undefined) return "-";
    return tf ? "Yes" : "No";
}


function fixSoftMetric(value: string, convert: boolean) {
    if(!convert) return value;

    if(value.indexOf("mm") !== -1) {
        return value;
    }

    const uu = universalParser(value);
    return MetricDistance.fromUniversal(uu).toString()
}